import React from "react";

/**
 * Radial SVG mindmap: center node + N branches × M leaves.
 * Pure CSS/SVG, no external deps. Colors alternate across four themes drawn
 * from the app's Organic/Earthy palette (sage / terracotta / amber / indigo).
 */
const BRANCH_COLORS = [
  { stroke: "#7a9e7e", fill: "#eaf2eb", text: "#3d5742" },   // sage
  { stroke: "#c4614a", fill: "#fbe9e3", text: "#6f2d21" },   // terracotta
  { stroke: "#c89a4b", fill: "#fbf1dc", text: "#6d4d17" },   // amber
  { stroke: "#6a7aa8", fill: "#e6ebf5", text: "#2f3a5e" },   // indigo
  { stroke: "#8a7ca8", fill: "#ece7f2", text: "#3f3258" },   // violet
  { stroke: "#4f8f94", fill: "#dff0f1", text: "#1f4b4e" },   // teal
];

export default function Mindmap({ data }) {
  if (!data || !Array.isArray(data.branches) || data.branches.length === 0) return null;

  const W = 960;
  const H = 560;
  const cx = W / 2;
  const cy = H / 2;
  const branches = data.branches.slice(0, 6);
  const N = branches.length;
  const branchRadius = 220;
  const leafRadius = 120;

  return (
    <div className="bg-white border border-border rounded-2xl p-4 overflow-x-auto" data-testid="lesson-mindmap">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto min-w-[640px]" role="img" aria-label="Chapter mindmap">
        <defs>
          <radialGradient id="mm-center" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#3d5742" />
            <stop offset="100%" stopColor="#7a9e7e" />
          </radialGradient>
        </defs>

        {/* connector + branch + leaves */}
        {branches.map((br, i) => {
          const c = BRANCH_COLORS[i % BRANCH_COLORS.length];
          const angle = (i / N) * Math.PI * 2 - Math.PI / 2;
          const bx = cx + Math.cos(angle) * branchRadius;
          const by = cy + Math.sin(angle) * branchRadius;
          const leaves = (br.children || []).slice(0, 5);
          const spread = Math.PI / 3; // arc width for leaves
          return (
            <g key={`br-${i}`}>
              {/* connector: center -> branch */}
              <path
                d={`M ${cx} ${cy} Q ${(cx + bx) / 2} ${(cy + by) / 2 - 18 * Math.sin(angle)} ${bx} ${by}`}
                stroke={c.stroke}
                strokeWidth="2.5"
                fill="none"
                opacity="0.7"
              />

              {/* leaves */}
              {leaves.map((leaf, j) => {
                const t = leaves.length === 1 ? 0 : (j / (leaves.length - 1)) - 0.5;
                const la = angle + t * spread;
                const lx = bx + Math.cos(la) * leafRadius;
                const ly = by + Math.sin(la) * leafRadius;
                const leafLen = Math.max(12, Math.min(26, String(leaf).length));
                return (
                  <g key={`leaf-${i}-${j}`}>
                    <line
                      x1={bx}
                      y1={by}
                      x2={lx}
                      y2={ly}
                      stroke={c.stroke}
                      strokeWidth="1.6"
                      opacity="0.5"
                    />
                    <rect
                      x={lx - leafLen * 3.6}
                      y={ly - 13}
                      width={leafLen * 7.2}
                      height="26"
                      rx="13"
                      fill={c.fill}
                      stroke={c.stroke}
                      strokeWidth="1.2"
                    />
                    <text
                      x={lx}
                      y={ly + 4}
                      textAnchor="middle"
                      fontSize="11"
                      fill={c.text}
                      style={{ fontFamily: "IBM Plex Sans, system-ui, sans-serif" }}
                    >
                      {truncate(String(leaf), 32)}
                    </text>
                  </g>
                );
              })}

              {/* branch label pill */}
              <rect
                x={bx - 78}
                y={by - 16}
                width="156"
                height="32"
                rx="16"
                fill={c.stroke}
              />
              <text
                x={bx}
                y={by + 5}
                textAnchor="middle"
                fontSize="13"
                fontWeight="600"
                fill="#fff"
                style={{ fontFamily: "Outfit, IBM Plex Sans, system-ui, sans-serif" }}
              >
                {truncate(String(br.label), 20)}
              </text>
            </g>
          );
        })}

        {/* center node */}
        <circle cx={cx} cy={cy} r="78" fill="url(#mm-center)" stroke="#3d5742" strokeWidth="2" />
        <text
          x={cx}
          y={cy + 5}
          textAnchor="middle"
          fontSize="14"
          fontWeight="700"
          fill="#fff"
          style={{ fontFamily: "Outfit, IBM Plex Sans, system-ui, sans-serif" }}
        >
          {wrap(String(data.center || ""), 16).map((ln, k, arr) => (
            <tspan key={`ctr-${k}`} x={cx} dy={k === 0 ? -6 * (arr.length - 1) : 14}>
              {ln}
            </tspan>
          ))}
        </text>
      </svg>
    </div>
  );
}

function truncate(s, n) {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}
function wrap(s, n) {
  if (s.length <= n) return [s];
  const words = s.split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > n) {
      if (cur) lines.push(cur.trim());
      cur = w;
    } else {
      cur = (cur + " " + w).trim();
    }
    if (lines.length >= 2) break;
  }
  if (cur && lines.length < 3) lines.push(truncate(cur, n));
  return lines.slice(0, 3);
}
